<template>
  <span class="pega-break">
    <div class="boss-table-title pega-print-title">
      <div class="pega-ta-right">
        <span data-test="company-name">{{ entryCompanyName }}
          <span
            v-if="headerUserName"
            data-test="header-info"
          >
            <br><span
              v-if="isDuplicatedEntries"
              class="boss-status-label pega-status-label pega-status-label-top-0"
              data-test="label-duplicated-entries"
            >重複応募
            </span>
            <span
              v-if="isSimpleEntryForm"
              class="boss-status-label pega-status-label pega-status-label-top-0"
              data-test="label-entry-form"
            >自己PR不要
            </span>
            応募者：{{ headerUserName }}様
          </span>
          <br><span data-test="updated-date">
            {{ resume.updatedDate | formatDate }}現在
          </span>
        </span>
      </div>
    </div>
    <div class="boss-layout boss-layout-gap-between">
      <div class="boss-resume-sheet-title pega-resume-sheet-title">履歴書</div>
    </div>
    <div class="boss-layout-block">
      <div class="boss-table-wrap">
        <table class="boss-table-4 boss-table-4-responsive pega-th-color-important pega-print-resume-table">
          <tr>
            <th>応募求人</th>
            <td
              colspan="4"
              data-test="job-name"
            >{{ resume.jobName }}</td>
            <td
              rowspan="6"
              class="boss-table-4-plain boss-resume-sheet-photo-space-cell padding-bottom-1"
            >
              <div class="boss-resume-sheet-photo-space-layout">
                <div class="is-boss-only-pc">
                  <div class="boss-resume-sheet-photo-space" />
                </div>
                <div data-test="user-id">会員ID：{{ resume.userId }}</div>
              </div>
            </td>
          </tr>
          <tr>
            <th>応募日</th>
            <td
              colspan="4"
              data-test="entry-date"
            >{{ resume.entryDate | formatDateTime }}</td>
          </tr>
          <tr>
            <td
              class="boss-table-4-space"
              colspan="4"
            />
          </tr>
          <tr>
            <th>フリガナ</th>
            <td
              colspan="4"
              data-test="name-kana"
            >{{ userNameKana }}</td>
          </tr>
          <tr>
            <th>氏名</th>
            <td
              colspan="4"
              data-test="name"
            >{{ userName }}</td>
          </tr>
          <tr>
            <th>生年月日</th>
            <td
              colspan="2"
              data-test="birthday"
            >{{ resume.birthday | formatDate }} {{ ageLabel }}</td>
            <th>性別</th>
            <td
              class="text-nowrap"
              data-test="gender"
            >{{ resume.gender | parseBlankForDefaultValue }}</td>
          </tr>
          <tr>
            <th rowspan="2">現住所</th>
            <td
              colspan="5"
              data-test="zip"
            >{{ formatZip }}</td>
          </tr>
          <tr>
            <td
              colspan="5"
              data-test="address"
            >{{ resume.address }}</td>
          </tr>
          <tr>
            <th>電話番号</th>
            <td
              data-test="tel"
              colspan="2"
            >{{ resume.tel | parseBlankForDefaultValue }}</td>
            <th>携帯電話番号</th>
            <td
              colspan="2"
              class="text-nowrap"
              data-test="mobile"
            >{{ resume.mobile | parseBlankForDefaultValue }}</td>
          </tr>
          <tr>
            <th>メールアドレス</th>
            <td
              colspan="2"
              data-test="email"
              class="pega-break"
            >{{ resume.email | parseBlankForDefaultValue }}</td>
            <th>携帯メールアドレス</th>
            <td
              colspan="2"
              data-test="email-mobile"
              class="pega-break"
            >{{ resume.emailMobile | parseBlankForDefaultValue }}</td>
          </tr>
          <tr>
            <td
              class="boss-table-4-space"
              colspan="6"
            />
          </tr>
          <tr>
            <th colspan="6">学歴</th>
          </tr>
          <tr>
            <td
              class="text-center text-unset-mobile"
              data-test="graduated-date"
            >{{ graduatedSchoolDate }}</td>
            <td
              colspan="5"
              data-test="graduated-school"
            >{{ resume.graduatedSchool.name }} {{ resume.graduatedSchool.department }} 卒業 (最終学歴:{{ resume.educationalBackgroundName | parseBlankForDefaultValue }})
              <div data-test="graduated-school-department-category">
                学部・学科コード: {{ resume.graduatedSchool.departmentCategory | parseBlankForDefaultValue }}
              </div>
            </td>
          </tr>
          <tr>
            <th colspan="6">職務経歴（概略）</th>
          </tr>
          <template v-if="resume.careerResumes.length">
            <tr
              v-for="(careerResume, i) in resume.careerResumes"
              :key="i"
            >
              <td
                class="text-center text-unset-mobile"
                :data-test="'working-period-' + i"
              >{{ workingPeriod(careerResume.firstYear, careerResume.firstMonth) }}～{{ workingPeriod(careerResume.lastYear, careerResume.lastMonth) }}</td>
              <td
                :data-test="'career-company-name-' + i"
                colspan="5"
              >{{ careerResume.companyName }}</td>
            </tr>
          </template>
          <template v-else>
            <tr>
              <td data-test="working-period-0">未登録</td>
              <td
                data-test="career-company-name-0"
                colspan="5"
              >未登録</td>
            </tr>
          </template>
          <tr>
            <td />
            <td
              colspan="5"
              class="boss-right"
            >以上</td>
          </tr>
          <tr>
            <td
              class="boss-table-4-space"
              colspan="6"
            />
          </tr>
          <tr>
            <th>学歴補足事項</th>
            <td
              colspan="5"
              data-test="educational-background-note"
              class="pega-pre-wrap"
            >{{ resume.graduatedSchool.educationalNote | parseBlankForDefaultValue() }}</td>
          </tr>
          <tr>
            <th>就業状況</th>
            <td
              colspan="3"
              data-test="working-status-name"
            >{{ resume.workingStatusName | parseBlankForDefaultValue }}</td>
            <th>転職の回数</th>
            <td
              colspan="1"
              data-test="change-job-count"
            >{{ changeJobCount }}</td>
          </tr>
          <tr>
            <td
              class="boss-table-4-space"
              colspan="6"
            />
          </tr>
          <tr>
            <th>資格</th>
            <td
              colspan="5"
              data-test="licenses"
            >{{ licenses }}</td>
          </tr>
          <tr>
            <th>その他の資格</th>
            <td
              colspan="5"
              data-test="other-license"
              class="pega-pre-wrap"
            >{{ resume.otherLicense | parseBlankForDefaultValue() }}</td>
          </tr>
          <tr>
            <td
              class="boss-table-4-space"
              colspan="6"
            />
          </tr>
          <tr>
            <th>TOEIC</th>
            <td
              data-test="toeic"
              colspan="2"
            >
              <hidden-notice v-if="isSimpleEntryForm" />
              <template v-else>{{ resume.toeic | parseBlankForDefaultValue('点') }}</template>
            </td>
            <th>TOEFL</th>
            <td
              colspan="2"
              data-test="toefl"
            >
              <hidden-notice v-if="isSimpleEntryForm" />
              <template v-else>{{ resume.toefl | parseBlankForDefaultValue('点') }}</template>
            </td>
          </tr>
          <tr>
            <th>英語スキル</th>
            <td
              data-test="english-skill"
              colspan="2"
            >
              <hidden-notice v-if="isSimpleEntryForm" />
              <template v-else>{{ resume.englishSkill | parseBlankForDefaultValue() }}</template>
            </td>
            <th>英語以外の語学スキル</th>
            <td
              colspan="2"
              data-test="other-language-skill"
            >
              <hidden-notice v-if="isSimpleEntryForm" />
              <template v-else>{{ otherLanguageSkillsText }}</template>
            </td>
          </tr>
          <tr>
            <td
              class="boss-table-4-space"
              colspan="6"
            />
          </tr>
          <tr>
            <th>志望動機・自己PR</th>
            <td
              v-if="isSimpleEntryForm"
              data-test="self-pr-simple"
              colspan="5"
            >
              <hidden-notice />
            </td>
            <td
              v-else
              colspan="5"
              class="pega-pre-wrap"
              data-test="self-pr"
              v-text="selfPr"
            />
          </tr>
          <tr>
            <td
              class="boss-table-4-space"
              colspan="6"
            />
          </tr>
          <tr>
            <th>本人希望記入欄</th>
            <td colspan="5">
              <dl class="boss-definition-list">
                <dt>希望勤務地：</dt>
                <dd data-test="desired-prefecture">{{ desiredPrefecture }}</dd>
                <dt>希望年収：</dt>
                <dd data-test="desired-salary">{{ desiredSalary }}</dd>
                <dt>希望転職時期：</dt>
                <dd data-test="desired-change-period">{{ desiredChangePeriod }}</dd>
                <dt>その他の希望：</dt>
                <dd data-test="desired-other">
                  <hidden-notice v-if="isSimpleEntryForm" />
                  <template v-else>{{ desiredOther }}</template>
                </dd>
                <template v-for="(questionsAnswer, i) in resume.questionsAnswers">
                  <dt
                    :key="'dt-' + i"
                    :data-test="'dt-' + i"
                    class="pega-pre-wrap"
                  >{{ questionsAnswer.question }}：</dt>
                  <dd
                    :key="'dd-' + i"
                    :data-test="'dd-' + i"
                    class="pega-pre-wrap"
                  >{{ questionsAnswer.answer }}</dd>
                </template>
              </dl>
            </td>
          </tr>
          <tr>
            <td
              class="boss-table-4-space"
              colspan="6"
            />
          </tr>
          <tr>
            <th>未既婚</th>
            <td data-test="marriage">
              <hidden-notice v-if="isSimpleEntryForm" />
              <template v-else>{{ resume.marriage | parseBlankForDefaultValue }}</template>
            </td>
            <th class="unset">子供の有無</th>
            <td data-test="child-status">
              <hidden-notice v-if="isSimpleEntryForm" />
              <template v-else>{{ resume.childStatus | parseBlankForDefaultValue }}</template>
            </td>
            <th class="unset">転居を伴う転職可否</th>
            <td data-test="home-moving">
              <hidden-notice v-if="isSimpleEntryForm" />
              <template v-else>{{ resume.homeMoving | parseBlankForDefaultValue }}</template>
            </td>
          </tr>
          <tr>
            <th>その他補足説明</th>
            <td
              data-test="other-supplement"
              colspan="5"
              class="pega-pre-wrap"
            >
              <hidden-notice v-if="isSimpleEntryForm" />
              <template v-else>{{ resume.otherSupplement | parseBlankForDefaultValue }}</template>
            </td>
          </tr>
        </table>
      </div>
    </div>
  </span>
</template>

<script>
import dayjs from 'dayjs'
import * as parse from '@/lib/parse'
import * as definition from '@/lib/labelValueMappings'
import hiddenNotice from '@/components/atoms/HiddenNotice'
import _ from 'lodash'

export default {
  components: {
    hiddenNotice
  },
  filters: {
    formatDate (date) {
      return date ? dayjs(date).format('YYYY年MM月DD日') : ''
    },
    formatDateTime (date) {
      return date ? dayjs(date).format('YYYY年MM月DD日 HH時mm分') : ''
    },
    parseBlankForDefaultValue (label, suffix = '') {
      return label ? label + suffix : '未登録'
    }
  },
  props: {
    entryId: {
      type: Number,
      required: true
    },
    entryCompanyName: {
      type: String,
      default: ''
    },
    isDuplicatedEntries: {
      type: Boolean,
      default: false
    },
    isSimpleEntryForm: {
      type: Boolean,
      default: false
    },
    headerUserName: {
      type: String,
      default: ''
    },
    parentResume: {
      type: Object,
      default: () => null
    }
  },
  data () {
    return {
      resume: {
        jobName: '',
        entryDate: '',
        firstname: '',
        firstnameKana: '',
        lastname: '',
        lastnameKana: '',
        birthday: '',
        companyName: '',
        updatedDate: '',
        userId: undefined,
        gender: '',
        zip: '',
        address: '',
        tel: '',
        mobile: '',
        email: '',
        emailMobile: '',
        educationalBackgroundName: '',
        graduatedSchool: {
          department: '',
          departmentCategory: '',
          name: '',
          year: undefined,
          month: undefined,
          educationalNote: ''
        },
        careerResumes: [],
        workingStatusName: '',
        changeJobCount: undefined,
        marriage: '',
        childStatus: '',
        homeMoving: '',
        otherSupplement: '',
        licenses: [],
        otherLicense: '',
        toeic: undefined,
        toefl: undefined,
        englishSkill: '',
        otherLanguageSkills: [],
        selfPr: '',
        desiredItem: {
          salary: undefined,
          prefectures: [],
          changePeriod: '',
          other: ''
        },
        questionsAnswers: []
      }
    }
  },
  computed: {
    userNameKana () {
      return [this.resume.lastnameKana, this.resume.firstnameKana].join(' ')
    },
    userName () {
      return [this.resume.lastname, this.resume.firstname].join(' ')
    },
    ageLabel () {
      const birthday = _.get(this.resume, 'birthday')
      return birthday ? `(満${parse.age(birthday)}歳)` : ''
    },
    graduatedSchoolDate () {
      const graduateDate = dayjs([this.resume.graduatedSchool.year, this.resume.graduatedSchool.month - 1])
      return graduateDate.isValid() ? graduateDate.format('YYYY年MM月') : ''
    },
    licenses () {
      const licenses = _.get(this.resume, 'licenses')
      return (licenses && licenses.length) ? licenses.join('、 ') : '未登録'
    },
    formatZip () {
      const zip = _.get(this.resume, 'zip')
      return zip ? `〒${zip}` : ''
    },
    desiredPrefecture () {
      const prefectures = _.get(this.resume, 'desiredItem.prefectures')
      return (prefectures && prefectures.length) ? prefectures.join(' 、 ') : '未登録'
    },
    desiredSalary () {
      const salary = _.get(this.resume, 'desiredItem.salary')
      return salary ? `${salary}万円以上` : '未登録'
    },
    desiredChangePeriod () {
      const period = _.get(this.resume, 'desiredItem.changePeriod')
      return period || '未登録'
    },
    desiredOther () {
      const other = _.get(this.resume, 'desiredItem.other')
      return other || '未登録'
    },
    selfPr () {
      const pr = _.get(this.resume, 'selfPr')
      return pr || '未登録'
    },
    changeJobCount () {
      const id = _.get(this.resume, 'changeJobCount')
      return _.isNumber(id) ? definition.job[id].name : '未登録'
    },
    otherLanguageSkillsText () {
      return this.resume.otherLanguageSkills.length
        ? this.resume.otherLanguageSkills.map(o => {
          return o.level.name ? `${o.name.name}(${o.level.name})` : `${o.name.name}`
        }).join('、')
        : '未登録'
    }
  },
  watch: {
    entryId: {
      handler () {
        this.fetchResume()
      }
    }
  },
  created () {
    this.fetchResume()
  },
  methods: {
    workingPeriod (year, month) {
      // 職務経歴書で9999が登録されている場合は現在就業中
      if (year === 9999) return '現在就業中'
      // 9999以外の日付が入力されている場合は登録されている値
      if (year && month) return dayjs([year, month - 1]).format('YYYY年MM月')
      return '未登録' // それ以外は未登録と扱う
    },
    fetchResume () {
      if (this.parentResume) {
        this.setResume(this.parentResume)
      } else {
        this.$http.secured.get(`/entry/view/entries/${this.entryId}/resume`).then(response => {
          this.setResume(response.data)
        })
      }
    },
    setResume (data) {
      this.$set(this, 'resume', data)
    }
  }
}

</script>
